import { normalizeError } from 'core/common/errors';
import { SpanAttributes, SpanName } from 'core/common/observability/entities';
import { ATTRS_ANALYTICS_EVENT_NAME } from 'core/common/observability/entities/SemanticAttributes';
import { ObservabilitySystem } from 'core/common/observability/interfaces';
import { ErrorMonitoring } from 'core/common/services/errorMonitoring';
import { BingAdsEventTypes } from '../../entities';
import { BingAdsService } from '../../interfaces';
import { BingAdsServiceDecorator } from './BingAdsServiceDecorator';

export class BingAdsServiceWithMonitoring extends BingAdsServiceDecorator {
  constructor(
    bingAdsService: BingAdsService,
    private readonly observabilitySystem: ObservabilitySystem,
    private readonly errorMonitoringService: ErrorMonitoring,
    private readonly receiveAdditionalObservabilityAttributes?: () => SpanAttributes,
  ) {
    super(bingAdsService);
  }

  track(eventName: BingAdsEventTypes, data: Record<string, unknown>) {
    if (!super.isUetqRequired()) {
      try {
        return super.track(eventName, data);
      } catch {
        this.errorMonitoringService.captureError('BingAds track failed', {
          tags: {
            error_message: 'BingAds track failed',
            code: 'bing_ads_track_failed_when_not_required',
          },
        });
        return;
      }
    }

    if (super.isUetqRequired() && !super.isUetqAvailable()) {
      try {
        return super.track(eventName, data);
      } catch {
        this.errorMonitoringService.captureError('BingAds track failed', {
          tags: {
            error_message: 'BingAds track failed',
            code: 'bing_ads_track_failed_when_required_not_available',
          },
        });
        return;
      }
    }

    const span = this.observabilitySystem.startSpan(SpanName.TRACK_ANALYTICS_BING_ADS);
    const spanAttributes = {
      [ATTRS_ANALYTICS_EVENT_NAME]: eventName,
      ...(this.receiveAdditionalObservabilityAttributes
        ? this.receiveAdditionalObservabilityAttributes()
        : {}),
    } as SpanAttributes;
    span.setAttributes(spanAttributes);

    try {
      const result = super.track(eventName, data);
      span.succeed();
      return result;
    } catch (err) {
      const error = normalizeError(err);
      span.fail(error);
      return;
    }
  }
}
